import {
  CopyIcon,
  ExternalLinkIcon,
  Loader2Icon,
  RefreshCwIcon,
  TriangleAlertIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { CopyableValue } from "@/components/commons/copyable-value"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { FRONTEND_ROUTES } from "@/constants"
import { formatMoney } from "@/helpers/format"
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard"
import { cn } from "@/lib/utils"
import { useReprocessWebhookInboxMutation } from "@/services/admin"
import type { WebhookInboxDetail } from "@/types/webhook-inbox"
import { DateHelper } from "@/utils"

import { formatPayload } from "../format-payload"
import {
  getWebhookInboxStatusClasses,
  isReprocessable,
} from "../status-config"

const EM_DASH = "—"

const formatDateTime = (value?: string) =>
  value
    ? DateHelper.formatDate(value, "dd/MM/yyyy HH:mm:ss")
    : EM_DASH

interface InfoRowProps {
  label: string
  children: React.ReactNode
}

const InfoRow = ({ label, children }: InfoRowProps) => (
  <div className="space-y-0.5">
    <p className="text-xs font-medium text-muted-foreground">
      {label}
    </p>
    <div className="break-words text-sm">{children}</div>
  </div>
)

interface WebhookInboxDetailSheetProps {
  detail?: WebhookInboxDetail
  isLoading: boolean
  isError?: boolean
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Painel de inspeção de um webhook recebido: metadados, correlação com o pedido
 * e a transação, payload cru e reprocesso.
 *
 * O payload é o corpo enviado pelo processador e pode conter dados do pagador —
 * fica só nesta tela (superadmin) e nunca vai para log ou storage.
 */
export const WebhookInboxDetailSheet = ({
  detail,
  isLoading,
  isError = false,
  isOpen,
  onOpenChange,
}: WebhookInboxDetailSheetProps) => {
  const { t } = useTranslation()
  const formattedPayload = formatPayload(detail?.payload)
  const copyToClipboard = useCopyToClipboard()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const reprocessMutation =
    useReprocessWebhookInboxMutation()

  const transaction = detail?.paymentTransaction
  // Webhook de tokenização não tem transação nem pedido: o cartão salvo na carteira é a
  // ÚNICA correlação que ele carrega, e o dono do cartão é o caminho para o cadastro.
  const creditCard = detail?.creditCard
  // O que a busca de pedidos aceita é o CÓDIGO; o uuid só serve para montar o link.
  const orderLabel =
    detail?.orderCode ?? detail?.orderId ?? ""

  const handleReprocess = () => {
    if (!detail?.id) return
    reprocessMutation.mutate(detail.id)
    setIsConfirmOpen(false)
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>
            {t("pages.adminWebhookInbox.detail.title")}
          </SheetTitle>
          <SheetDescription>
            {t("pages.adminWebhookInbox.detail.subtitle")}
          </SheetDescription>
        </SheetHeader>

        {isLoading && (
          <div className="space-y-3">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {!isLoading && isError && (
          <div className="flex flex-col items-center gap-3 py-10">
            <TriangleAlertIcon
              size={40}
              strokeWidth={1.5}
              className="text-red-600"
            />
            <p className="text-sm text-muted-foreground">
              {t(
                "pages.adminWebhookInbox.detail.loadError"
              )}
            </p>
          </div>
        )}

        {!isLoading && !isError && detail && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <CopyableValue
                value={detail.id}
                label={t(
                  "pages.adminWebhookInbox.detail.webhookId"
                )}
              >
                <span className="font-mono text-sm">
                  {detail.id}
                </span>
              </CopyableValue>
              <Badge
                variant="none"
                className={cn(
                  "rounded-full border px-2 py-1 font-medium",
                  getWebhookInboxStatusClasses(
                    detail.status
                  )
                )}
              >
                {t(
                  `pages.adminWebhookInbox.status.${detail.status}`
                )}
              </Badge>
            </div>

            <section className="grid grid-cols-2 gap-4 rounded-md border p-4">
              <InfoRow
                label={t(
                  "pages.adminWebhookInbox.table.processor"
                )}
              >
                {detail.processor
                  ? t(
                      `pages.adminWebhookInbox.processors.${detail.processor}`
                    )
                  : EM_DASH}
              </InfoRow>
              <InfoRow
                label={t(
                  "pages.adminWebhookInbox.table.endpoint"
                )}
              >
                {detail.endpoint
                  ? t(
                      `pages.adminWebhookInbox.endpoints.${detail.endpoint}`
                    )
                  : EM_DASH}
              </InfoRow>
              <InfoRow
                label={t(
                  "pages.adminWebhookInbox.table.whiteLabel"
                )}
              >
                {detail.whiteLabelId ?? EM_DASH}
              </InfoRow>
              <InfoRow
                label={t(
                  "pages.adminWebhookInbox.table.attempts"
                )}
              >
                {detail.attempts ?? 0}
              </InfoRow>
              <InfoRow
                label={t(
                  "pages.adminWebhookInbox.table.receivedAt"
                )}
              >
                {formatDateTime(detail.receivedAt)}
              </InfoRow>
              <InfoRow
                label={t(
                  "pages.adminWebhookInbox.detail.processedAt"
                )}
              >
                {formatDateTime(detail.processedAt)}
              </InfoRow>
              {detail.lastError && (
                <div className="col-span-2">
                  <InfoRow
                    label={t(
                      "pages.adminWebhookInbox.detail.lastError"
                    )}
                  >
                    <CopyableValue
                      value={detail.lastError}
                      label={t(
                        "pages.adminWebhookInbox.detail.lastError"
                      )}
                    >
                      <span className="text-red-700">
                        {detail.lastError}
                      </span>
                    </CopyableValue>
                  </InfoRow>
                </div>
              )}
            </section>

            <section className="space-y-3 rounded-md border p-4">
              <h3 className="text-sm font-semibold">
                {t(
                  "pages.adminWebhookInbox.detail.correlation"
                )}
              </h3>
              {detail.orderId || transaction || creditCard ? (
                <div className="grid grid-cols-2 gap-4">
                  {detail.orderId && (
                    <div className="col-span-2">
                      <InfoRow
                        label={t(
                          "pages.adminWebhookInbox.detail.order"
                        )}
                      >
                        <CopyableValue
                          value={orderLabel}
                          label={t(
                            "pages.adminWebhookInbox.detail.order"
                          )}
                        >
                          <Link
                            to={`${FRONTEND_ROUTES.ORDERS.href}/details/${detail.orderId}`}
                            className="inline-flex items-center gap-1 text-primary underline"
                          >
                            {orderLabel}
                            <ExternalLinkIcon size={14} />
                          </Link>
                        </CopyableValue>
                      </InfoRow>
                    </div>
                  )}
                  {transaction && (
                    <>
                      <InfoRow
                        label={t(
                          "pages.adminWebhookInbox.detail.transaction"
                        )}
                      >
                        <CopyableValue
                          value={transaction.id}
                          label={t(
                            "pages.adminWebhookInbox.detail.transaction"
                          )}
                        />
                      </InfoRow>
                      <InfoRow
                        label={t(
                          "pages.adminWebhookInbox.detail.transactionStatus"
                        )}
                      >
                        {transaction.status ?? EM_DASH}
                      </InfoRow>
                      <InfoRow
                        label={t(
                          "pages.adminWebhookInbox.detail.paymentMethod"
                        )}
                      >
                        {transaction.paymentMethod ??
                          EM_DASH}
                      </InfoRow>
                      <InfoRow
                        label={t(
                          "pages.adminWebhookInbox.detail.amount"
                        )}
                      >
                        <span className="tabular-nums">
                          {typeof transaction.amount ===
                          "number"
                            ? formatMoney(
                                transaction.amount
                              )
                            : EM_DASH}
                        </span>
                      </InfoRow>
                    </>
                  )}
                  {creditCard && (
                    <>
                      <InfoRow
                        label={t(
                          "pages.adminWebhookInbox.detail.cardBrand"
                        )}
                      >
                        {creditCard.brand ?? EM_DASH}
                      </InfoRow>
                      <InfoRow
                        label={t(
                          "pages.adminWebhookInbox.detail.cardStatus"
                        )}
                      >
                        {creditCard.status
                          ? t(
                              `pages.adminWebhookInbox.cardStatus.${creditCard.status}`,
                              // Status novo no payment aparece cru em vez de virar a chave
                              // literal na tela — o operador ainda consegue ler o que veio.
                              {
                                defaultValue: creditCard.status,
                              }
                            )
                          : EM_DASH}
                      </InfoRow>
                      <div className="col-span-2">
                        <InfoRow
                          label={t(
                            "pages.adminWebhookInbox.detail.customer"
                          )}
                        >
                          {creditCard.customerId ? (
                            <CopyableValue
                              value={creditCard.customerId}
                              label={t(
                                "pages.adminWebhookInbox.detail.customer"
                              )}
                            >
                              <Link
                                to={`${FRONTEND_ROUTES.CUSTOMERS.href}/details/${creditCard.customerId}`}
                                className="inline-flex items-center gap-1 font-mono text-xs text-primary underline"
                              >
                                {creditCard.customerId}
                                <ExternalLinkIcon size={14} />
                              </Link>
                            </CopyableValue>
                          ) : (
                            EM_DASH
                          )}
                        </InfoRow>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t(
                    "pages.adminWebhookInbox.detail.noCorrelation"
                  )}
                </p>
              )}
            </section>

            {detail.payload && (
              <section className="space-y-2 rounded-md border p-4">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold">
                    {t(
                      "pages.adminWebhookInbox.detail.payload"
                    )}
                  </h3>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2"
                    onClick={() =>
                      // Copia o payload JÁ FORMATADO: é o que está na tela e o que o
                      // suporte cola no chamado.
                      void copyToClipboard(formattedPayload)
                    }
                  >
                    <CopyIcon size={14} className="mr-1" />
                    {t("common.copy")}
                  </Button>
                </div>
                <pre className="max-h-72 overflow-auto rounded bg-muted p-3 font-mono text-xs">
                  {formattedPayload}
                </pre>
              </section>
            )}

            {isReprocessable(detail.status) && (
              <Button
                type="button"
                onClick={() => setIsConfirmOpen(true)}
                disabled={reprocessMutation.isPending}
              >
                {reprocessMutation.isPending ? (
                  <Loader2Icon
                    size={16}
                    className="mr-2 animate-spin"
                  />
                ) : (
                  <RefreshCwIcon
                    size={16}
                    className="mr-2"
                  />
                )}
                {t("pages.adminWebhookInbox.reprocess")}
              </Button>
            )}
          </>
        )}

        <AlertDialog
          open={isConfirmOpen}
          onOpenChange={setIsConfirmOpen}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t(
                  "pages.adminWebhookInbox.confirmReprocessTitle"
                )}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t(
                  "pages.adminWebhookInbox.confirmReprocessDescription"
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("common.cancel")}
              </AlertDialogCancel>
              <AlertDialogAction onClick={handleReprocess}>
                {t("common.confirm")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  )
}
